import React, { useMemo } from 'react';
import { Skeleton } from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  DragIndicatorRounded,
  RocketLaunchOutlined,
} from '@mui/icons-material';
import Text from '../../Text';
import './styles.scss';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toUnit(val) {
  if (val == null) return null;
  return typeof val === 'number' ? `${val}px` : String(val);
}

/**
 * Build the CSS grid-template-columns string from the ordered columns array.
 * Appends one extra `max-content` track when an actionsComponent is present.
 */
function calcGridTemplate(columns, hasActions) {
  const parts = columns.map((col) => {
    const min = toUnit(col.minWidth);
    const max = toUnit(col.maxWidth);

    if (col.grow != null) {
      return `minmax(${min ?? 'max-content'}, ${col.grow}fr)`;
    }
    if (min && max) return `minmax(${min}, ${max})`;
    if (min) return `minmax(${min}, max-content)`;
    if (max) return `minmax(max-content, ${max})`;
    return 'max-content';
  });

  if (hasActions) parts.push('max-content');

  return parts.join(' ');
}

function isColDraggable(col, globalDraggable) {
  if (!globalDraggable) return false;
  // allowReorder defaults to true if not specified
  if (col.allowReorder === false) return false;
  // sticky columns are never reorderable by user
  if (col.stickyAtStart || col.stickyAtEnd) return false;
  return true;
}

function getStickyMod(col) {
  if (col.stickyAtStart) return ' frag-table__cell--sticky-start';
  if (col.stickyAtEnd) return ' frag-table__cell--sticky-end';
  return '';
}

function getAlignMod(col) {
  if (col.align === 'center') return ' frag-table__cell--center';
  if (col.align === 'right') return ' frag-table__cell--right';
  return '';
}

function getDisplayMod(col) {
  if (col.display === 'column') return ' frag-table__cell--display-col';
  return '';
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function EmptyState({ emptyIcon, emptyMessage, emptySubtitle }) {
  return (
    <div className="frag-table__empty">
      <div className="frag-table__empty-icon">
        {emptyIcon ?? <RocketLaunchOutlined />}
      </div>
      <Text.p contrast={7} weight={500} style={{ margin: 0 }}>
        {emptyMessage}
      </Text.p>
      {emptySubtitle && (
        <Text.p contrast={4} style={{ margin: 0 }}>
          {emptySubtitle}
        </Text.p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main view
// ---------------------------------------------------------------------------

export default function TableView({
  // from useTable
  orderedColumns,
  dragState,
  handleDragStart,
  handleDragOver,
  handleDrop,
  handleDragEnd,
  // data props
  data,
  loading,
  loadingRows,
  emptyMessage,
  emptySubtitle,
  emptyIcon,
  // layout
  fullWidth,
  contained,
  draggable,
  // pagination
  enablePagination,
  pages,
  records,
  page,
  onSelectPage,
  // row interaction
  onRowClick,
  rowClassName,
  // actions column
  actionsComponent,
  // header sticky offset (px or CSS string)
  headerTop,
  className,
}) {
  const hasActions = Boolean(actionsComponent);

  const gridTemplate = useMemo(
    () => calcGridTemplate(orderedColumns, hasActions),
    [orderedColumns, hasActions]
  );

  const containerClass = [
    'frag-table',
    contained ? 'frag-table--contained' : '',
    fullWidth ? 'frag-table--full-width' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // -------------------------------------------------------------------------
  // Header cell
  // -------------------------------------------------------------------------
  const renderHeaderCell = (col, visualIndex) => {
    const canDrag = isColDraggable(col, draggable);
    const isDragging = dragState.sourceIndex === visualIndex;
    const isDragOver =
      dragState.targetIndex === visualIndex &&
      dragState.sourceIndex !== null &&
      dragState.sourceIndex !== visualIndex;

    const cls = [
      'frag-table__cell',
      'frag-table__header-cell',
      getStickyMod(col),
      getAlignMod(col),
      canDrag ? 'frag-table__header-cell--draggable' : '',
      isDragging ? 'frag-table__header-cell--dragging' : '',
      isDragOver ? 'frag-table__header-cell--drag-over' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div
        key={visualIndex}
        className={cls}
        draggable={canDrag || undefined}
        onDragStart={
          canDrag
            ? (e) => {
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', String(visualIndex));
                handleDragStart(visualIndex);
              }
            : undefined
        }
        onDragOver={
          canDrag
            ? (e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                handleDragOver(visualIndex);
              }
            : undefined
        }
        onDrop={
          canDrag
            ? (e) => {
                e.preventDefault();
                handleDrop(visualIndex);
              }
            : undefined
        }
        onDragEnd={handleDragEnd}
      >
        {col.title != null && (
          <Text.h5 contrast={5} weight={500}>
            {col.title}
          </Text.h5>
        )}
        {canDrag && (
          <span className="frag-table__drag-handle">
            <DragIndicatorRounded style={{ fontSize: 20 }} />
          </span>
        )}
      </div>
    );
  };

  // -------------------------------------------------------------------------
  // Body cell
  // -------------------------------------------------------------------------
  const renderCell = (col, item, colIndex) => {
    const cls = [
      'frag-table__cell',
      getStickyMod(col),
      getAlignMod(col),
      getDisplayMod(col),
      col.className || '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div key={colIndex} className={cls}>
        {col.customRender ? (
          col.customRender(item)
        ) : col.render ? (
          <Text.span contrast={8}>{col.render(item)}</Text.span>
        ) : (
          <Text.span contrast={8}>{item[col.field] ?? ''}</Text.span>
        )}
      </div>
    );
  };

  // -------------------------------------------------------------------------
  // Data row
  // -------------------------------------------------------------------------
  const renderRow = (item, rowIndex) => {
    const extraClass =
      typeof rowClassName === 'function' ? rowClassName(item) : rowClassName || '';
    const cls = [
      'frag-table__row',
      onRowClick ? 'frag-table__row--clickable' : '',
      extraClass,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div
        key={item.id ?? rowIndex}
        className={cls}
        style={{ gridTemplateColumns: 'subgrid' }}
        onClick={onRowClick ? (e) => onRowClick(item, e) : undefined}
      >
        {orderedColumns.map((col, colIndex) => renderCell(col, item, colIndex))}
        {hasActions && (
          <div className="frag-table__cell frag-table__cell--sticky-end frag-table__actions-cell">
            {actionsComponent(item)}
          </div>
        )}
      </div>
    );
  };

  // -------------------------------------------------------------------------
  // Loading row
  // -------------------------------------------------------------------------
  const renderLoadingRow = (rowIndex) => (
    <div
      key={`loading-${rowIndex}`}
      className="frag-table__row"
      style={{ gridTemplateColumns: 'subgrid' }}
    >
      {orderedColumns.map((col, colIndex) => (
        <div key={colIndex} className="frag-table__cell">
          <Skeleton
            variant="text"
            width={col.loadingWidth ?? 100}
            height={16}
            sx={{ bgcolor: 'rgba(255,255,255,0.06)' }}
          />
        </div>
      ))}
      {hasActions && (
        <div className="frag-table__cell frag-table__actions-cell">
          <Skeleton
            variant="text"
            width={36}
            height={16}
            sx={{ bgcolor: 'rgba(255,255,255,0.06)' }}
          />
        </div>
      )}
    </div>
  );

  // -------------------------------------------------------------------------
  // Pagination
  // -------------------------------------------------------------------------
  const renderPagination = () => {
    if (!enablePagination || records === 0) return null;

    const go = (p) => {
      if (onSelectPage && p >= 1 && p <= pages) onSelectPage(p);
    };

    // Compute visible page range (max 5 page numbers)
    const maxVisible = 5;
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    let end = Math.min(pages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);

    const buttons = [];

    // ← Prev
    buttons.push(
      <button
        key="prev"
        className="frag-table__page-btn frag-table__page-btn--arrow"
        onClick={() => go(page - 1)}
        disabled={page <= 1}
        aria-label="Página anterior"
      >
        <ChevronLeft style={{ fontSize: 18 }} />
      </button>
    );

    // First page + ellipsis
    if (start > 1) {
      buttons.push(
        <button key="first" className="frag-table__page-btn" onClick={() => go(1)}>
          1
        </button>
      );
      if (start > 2) {
        buttons.push(
          <span key="dots-start" className="frag-table__page-dots">
            …
          </span>
        );
      }
    }

    // Middle page numbers
    for (let i = start; i <= end; i++) {
      buttons.push(
        <button
          key={i}
          className={`frag-table__page-btn${i === page ? ' frag-table__page-btn--active' : ''}`}
          onClick={() => go(i)}
        >
          {i}
        </button>
      );
    }

    // Ellipsis + last page
    if (end < pages) {
      if (end < pages - 1) {
        buttons.push(
          <span key="dots-end" className="frag-table__page-dots">
            …
          </span>
        );
      }
      buttons.push(
        <button key="last" className="frag-table__page-btn" onClick={() => go(pages)}>
          {pages}
        </button>
      );
    }

    // → Next
    buttons.push(
      <button
        key="next"
        className="frag-table__page-btn frag-table__page-btn--arrow"
        onClick={() => go(page + 1)}
        disabled={page >= pages}
        aria-label="Próxima página"
      >
        <ChevronRight style={{ fontSize: 18 }} />
      </button>
    );

    return (
      <div className="frag-table__pagination">
        <Text.span contrast={4}>
          Exibindo {data.length} de {records}
        </Text.span>
        {pages > 1 && (
          <div className="frag-table__pagination-controls">{buttons}</div>
        )}
      </div>
    );
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  const headerTopValue = headerTop != null
    ? (typeof headerTop === 'number' ? `${headerTop}px` : headerTop)
    : '0px';

  return (
    <div
      className={containerClass}
      style={{ '--frag-table-header-top': headerTopValue }}
    >
      <div className="frag-table__scroll-area">
        <div
          className="frag-table__grid"
          style={{ gridTemplateColumns: gridTemplate }}
        >
          {/* ── Header ─────────────────────────────────────────────────── */}
          <div
            className="frag-table__header"
            style={{ gridTemplateColumns: 'subgrid' }}
          >
            {orderedColumns.map(renderHeaderCell)}
            {hasActions && (
              <div className="frag-table__cell frag-table__header-cell frag-table__actions-col" />
            )}
          </div>

          {/* ── Body ───────────────────────────────────────────────────── */}
          {loading ? (
            <div className="frag-table__body">
              {Array.from({ length: loadingRows }, (_, i) => renderLoadingRow(i))}
            </div>
          ) : data.length === 0 ? (
            <div className="frag-table__empty-wrapper">
              <EmptyState
                emptyIcon={emptyIcon}
                emptyMessage={emptyMessage}
                emptySubtitle={emptySubtitle}
              />
            </div>
          ) : (
            <div className="frag-table__body">
              {data.map(renderRow)}
            </div>
          )}
        </div>
      </div>

      {/* ── Pagination ─────────────────────────────────────────────── */}
      {renderPagination()}
    </div>
  );
}
